import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Typography, Button, Tag, Space,
  Spin, message, Table, Modal, Form, Input,
  DatePicker, Select, Tabs, Alert, Popover, Badge, Row, Col
} from 'antd';
import {
  ScheduleOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SwapOutlined,
  PlusOutlined,
  HistoryOutlined,
  LeftOutlined,
  RightOutlined,
  CoffeeOutlined,
  ThunderboltOutlined,
  FieldTimeOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import StaffLayout from './StaffLayout';
import api from '../api';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const StaffRoster = () => {
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [loading, setLoading] = useState(true);
  const [rosterDays, setRosterDays] = useState([]);
  const [summary, setSummary] = useState(null);
  const [activeShifts, setActiveShifts] = useState([]);
  const [activeTab, setActiveTab] = useState('list');
  const [filterType, setFilterType] = useState('ALL');

  // Change Requests state
  const [changeRequests, setChangeRequests] = useState([]);
  const [requestModalVisible, setRequestModalVisible] = useState(false);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [requestForm] = Form.useForm();
  const [selectedDayForModal, setSelectedDayForModal] = useState(null);

  // Selected day for Calendar popup
  const [selectedCalendarDay, setSelectedCalendarDay] = useState(null);

  // Load monthly roster data
  const loadRosterData = async () => {
    setLoading(true);
    try {
      const monthKey = selectedMonth.format('YYYY-MM');
      const [rosterRes, reqRes] = await Promise.all([
        api.get(`/roster/me?month=${monthKey}`).catch(() => ({ data: null })),
        api.get('/roster/requests/my').catch(() => ({ data: null }))
      ]);

      if (rosterRes.data?.success) {
        setRosterDays(rosterRes.data.days || []);
        setSummary(rosterRes.data.summary || null);
        setActiveShifts(rosterRes.data.shifts || []);
      }

      if (reqRes.data?.success) {
        setChangeRequests(reqRes.data.requests || []);
      }
    } catch (err) {
      console.error('Failed to load roster:', err);
      message.error('Failed to load roster schedule');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRosterData();
  }, [selectedMonth]);

  // Calendar Grid Matrix calculation
  const calendarGrid = useMemo(() => {
    const year = selectedMonth.year();
    const month = selectedMonth.month(); // 0-indexed
    const firstDayIndex = new Date(year, month, 1).getDay(); // 0 is Sunday
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const daysMap = {};
    rosterDays.forEach(d => {
      if (d.date) daysMap[d.date] = d;
    });

    const cells = [];
    for (let i = 0; i < firstDayIndex; i++) {
      cells.push({ isEmpty: true, key: `empty-start-${i}` });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({
        isEmpty: false,
        dayNum: d,
        date: dateStr,
        record: daysMap[dateStr] || null,
        key: dateStr
      });
    }
    while (cells.length % 7 !== 0) {
      cells.push({ isEmpty: true, key: `empty-end-${cells.length}` });
    }
    return cells;
  }, [selectedMonth, rosterDays]);

  // Handle open request modal for a specific day
  const handleOpenRequestModal = (dayRecord) => {
    requestForm.resetFields();
    if (dayRecord) {
      setSelectedDayForModal(dayRecord);
      requestForm.setFieldsValue({
        date: dayjs(dayRecord.date),
        requestedStatus: 'SHIFT',
        requestedShiftTemplateId: activeShifts[0]?.id
      });
    } else {
      setSelectedDayForModal(null);
      requestForm.setFieldsValue({
        date: dayjs().add(1, 'day'),
        requestedStatus: 'SHIFT',
        requestedShiftTemplateId: activeShifts[0]?.id
      });
    }
    setRequestModalVisible(true);
  };

  // Submit Roster Change Request
  const handleSubmitChangeRequest = async (values) => {
    setSubmittingRequest(true);
    try {
      const payload = {
        date: values.date.format('YYYY-MM-DD'),
        requestedStatus: values.requestedStatus || 'SHIFT',
        requestedShiftTemplateId: values.requestedStatus === 'SHIFT' ? values.requestedShiftTemplateId : null,
        reason: values.reason
      };

      const res = await api.post('/roster/request-change', payload);
      if (res.data?.success) {
        message.success('Roster change request submitted successfully!');
        setRequestModalVisible(false);
        requestForm.resetFields();
        loadRosterData();
      } else {
        message.error(res.data?.message || 'Failed to submit request');
      }
    } catch (err) {
      console.error('Request submit error:', err);
      message.error(err.response?.data?.message || 'Error submitting roster change request');
    } finally {
      setSubmittingRequest(false);
    }
  };

  // Filtered rows for list view
  const filteredRoster = useMemo(() => {
    const todayStr = dayjs().format('YYYY-MM-DD');
    return rosterDays.filter(d => {
      if (filterType === 'ALL') return true;
      if (filterType === 'UPCOMING') return d.date >= todayStr;
      if (filterType === 'HISTORY') return d.date < todayStr;
      if (filterType === 'DAY_SHIFT') return d.dayType === 'SHIFT' && !d.isNightShift;
      if (filterType === 'NIGHT_SHIFT') return d.dayType === 'SHIFT' && d.isNightShift;
      if (filterType === 'OFF') return d.dayType === 'WEEKLY_OFF' || d.dayType === 'HOLIDAY';
      return true;
    });
  }, [rosterDays, filterType]);

  const todayShift = summary?.todayShift;

  return (
    <StaffLayout>
      <style>{`
        .roster-calendar-cell:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08) !important;
        }
      `}</style>

      {/* 1. HEADER & ACTIONS */}
      <Card
        style={{
          borderRadius: 16,
          border: '1px solid #e2e8f0',
          marginBottom: 20,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
        }}
        bodyStyle={{ padding: 24 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <Space align="center" size={14}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: '#eff6ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#2563eb',
              fontSize: 24
            }}>
              <ScheduleOutlined />
            </div>
            <div>
              <Title level={4} style={{ margin: 0, color: '#0f172a', fontWeight: 700 }}>
                {monthNames[selectedMonth.month()]} {selectedMonth.year()} Roster
              </Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                View assigned shift timings, Day/Night duty schedule, weekly offs & request shift swaps
              </Text>
            </div>
          </Space>

          <Space size={12} wrap>
            <DatePicker
              picker="month"
              value={selectedMonth}
              onChange={(val) => { if (val) setSelectedMonth(val); }}
              allowClear={false}
              format="MMM YYYY"
              style={{ borderRadius: 8, width: 130 }}
            />

            <Button.Group>
              <Button icon={<LeftOutlined />} onClick={() => setSelectedMonth(prev => prev.subtract(1, 'month'))} title="Previous Month" />
              <Button onClick={() => setSelectedMonth(dayjs())}>Current Month</Button>
              <Button icon={<RightOutlined />} onClick={() => setSelectedMonth(prev => prev.add(1, 'month'))} title="Next Month" />
            </Button.Group>

            <Button
              type="primary"
              icon={<SwapOutlined />}
              onClick={() => handleOpenRequestModal(null)}
              style={{
                borderRadius: 8,
                background: '#2563eb',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)'
              }}
            >
              Request Shift Change
            </Button>
          </Space>
        </div>
      </Card>

      {/* 2. TODAY'S SHIFT CARD & SUMMARY STATS */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {/* Current / Today's Shift Card */}
        <Col xs={24} md={10} lg={8}>
          <Card
            style={{
              borderRadius: 14,
              border: '1px solid #bfdbfe',
              background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)',
              height: '100%',
              boxShadow: '0 2px 6px rgba(37, 99, 235, 0.06)'
            }}
            bodyStyle={{ padding: 20 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <Tag color="blue" style={{ fontSize: 11, fontWeight: 700, borderRadius: 6, margin: 0 }}>
                TODAY'S SHIFT
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {dayjs().format('ddd, DD MMM YYYY')}
              </Text>
            </div>

            {todayShift ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 24 }}>
                    {todayShift.dayType === 'WEEKLY_OFF'
                      ? '☕'
                      : todayShift.dayType === 'HOLIDAY'
                      ? '🎉'
                      : todayShift.isNightShift
                      ? '🌙'
                      : '☀️'}
                  </span>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                      {todayShift.shiftName}
                    </div>
                    <div style={{ fontSize: 13, color: '#2563eb', fontWeight: 600, marginTop: 4 }}>
                      <ClockCircleOutlined style={{ marginRight: 5 }} />
                      {todayShift.time}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
                  <Tag color="green" style={{ borderRadius: 6, fontWeight: 600 }}>
                    Active Duty
                  </Tag>
                </div>
              </div>
            ) : (
              <div style={{ padding: '16px 0', color: '#64748b' }}>
                No active duty shift scheduled for today.
              </div>
            )}
          </Card>
        </Col>

        {/* Monthly Roster Metrics */}
        <Col xs={24} md={14} lg={16}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            gap: 12,
            height: '100%'
          }}>
            {[
              { label: 'Total Duty Shifts', value: summary?.totalDutyDays || 0, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', icon: '📋' },
              { label: 'Day Shifts', value: summary?.dayShiftsCount || 0, color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: '☀️' },
              { label: 'Night Shifts', value: summary?.nightShiftsCount || 0, color: '#7c3aed', bg: '#faf5ff', border: '#e9d5ff', icon: '🌙' },
              { label: 'Weekly Offs', value: summary?.weeklyOffCount || 0, color: '#0369a1', bg: '#f0f9ff', border: '#bae6fd', icon: '☕' },
              { label: 'Holidays', value: summary?.holidayCount || 0, color: '#db2777', bg: '#fdf2f8', border: '#fbcfe8', icon: '🎉' },
            ].map(m => (
              <div
                key={m.label}
                style={{
                  background: m.bg,
                  border: `1px solid ${m.border}`,
                  borderRadius: 14,
                  padding: '16px 14px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: 8,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: m.color }}>{m.label}</span>
                  <span style={{ fontSize: 18 }}>{m.icon}</span>
                </div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>
                  {m.value}
                </div>
              </div>
            ))}
          </div>
        </Col>
      </Row>

      {/* 3. MAIN TABS: List View vs Interactive Roster Calendar vs Change Requests */}
      <Card
        style={{
          borderRadius: 16,
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
        }}
        bodyStyle={{ padding: 24 }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'list',
              label: (
                <span>
                  <FieldTimeOutlined />
                  Upcoming & Shift History
                  {rosterDays.length > 0 && (
                    <Badge count={rosterDays.length} style={{ marginLeft: 6, backgroundColor: '#2563eb' }} />
                  )}
                </span>
              ),
              children: (
                <div>
                  {/* Filter Pills */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                    <Space wrap>
                      {[
                        { key: 'ALL', label: 'All Shifts' },
                        { key: 'UPCOMING', label: 'Upcoming Shifts' },
                        { key: 'HISTORY', label: 'Past History' },
                        { key: 'DAY_SHIFT', label: 'Day Shifts' },
                        { key: 'NIGHT_SHIFT', label: 'Night Shifts' },
                        { key: 'OFF', label: 'Weekly Offs & Holidays' },
                      ].map(f => (
                        <Button
                          key={f.key}
                          size="small"
                          type={filterType === f.key ? 'primary' : 'default'}
                          onClick={() => setFilterType(f.key)}
                          style={{ borderRadius: 6, fontWeight: 500 }}
                        >
                          {f.label}
                        </Button>
                      ))}
                    </Space>

                    <Button
                      type="primary"
                      icon={<SwapOutlined />}
                      onClick={() => handleOpenRequestModal(null)}
                      style={{ borderRadius: 6 }}
                    >
                      Request Shift Change
                    </Button>
                  </div>

                  {loading ? (
                    <div style={{ textAlign: 'center', padding: '50px 0' }}>
                      <Spin size="large" tip="Loading roster schedule..." />
                    </div>
                  ) : (
                    /* Exact format as requested in user's prompt & image: Date | Shift | Time | Status */
                    <Table
                      dataSource={filteredRoster}
                      rowKey="date"
                      pagination={{ pageSize: 12 }}
                      size="middle"
                      columns={[
                        {
                          title: 'Date',
                          dataIndex: 'date',
                          key: 'date',
                          render: (_, r) => (
                            <div>
                              <Text strong style={{ color: '#0f172a' }}>
                                {dayjs(r.date).format('DD MMM')}
                              </Text>
                              <div style={{ fontSize: 11, color: '#64748b' }}>
                                {r.dayOfWeekFull}
                              </div>
                            </div>
                          )
                        },
                        {
                          title: 'Shift',
                          key: 'shift',
                          render: (_, r) => {
                            if (r.dayType === 'WEEKLY_OFF') {
                              return <Tag color="blue" icon={<CoffeeOutlined />} style={{ borderRadius: 6, fontWeight: 600 }}>Weekly Off</Tag>;
                            }
                            if (r.dayType === 'HOLIDAY') {
                              return <Tag color="purple" icon={<ThunderboltOutlined />} style={{ borderRadius: 6, fontWeight: 600 }}>Holiday</Tag>;
                            }
                            if (r.dayType === 'LEAVE') {
                              return <Tag color="gold" style={{ borderRadius: 6, fontWeight: 600 }}>Leave</Tag>;
                            }
                            return (
                              <Tag
                                color={r.isNightShift ? 'purple' : 'gold'}
                                style={{ borderRadius: 6, fontWeight: 600 }}
                              >
                                {r.isNightShift ? '🌙 Night' : '☀️ Day'}
                              </Tag>
                            );
                          }
                        },
                        {
                          title: 'Shift Name & Time',
                          key: 'time',
                          render: (_, r) => (
                            <div>
                              <div style={{ fontWeight: 600, color: '#1e293b' }}>
                                {r.shiftName}
                              </div>
                              <div style={{ fontSize: 12, color: '#2563eb', fontWeight: 500 }}>
                                {r.time}
                              </div>
                            </div>
                          )
                        },
                        {
                          title: 'Status',
                          dataIndex: 'status',
                          key: 'status',
                          render: (st, r) => {
                            if (r.status === 'Today') {
                              return <Tag color="green" style={{ borderRadius: 6, fontWeight: 700 }}>Today</Tag>;
                            }
                            if (r.status === 'Upcoming') {
                              return <Tag color="blue" style={{ borderRadius: 6, fontWeight: 600 }}>Upcoming</Tag>;
                            }
                            return <Tag style={{ borderRadius: 6, color: '#94a3b8' }}>Completed</Tag>;
                          }
                        },
                        {
                          title: 'Action',
                          key: 'action',
                          render: (_, r) => (
                            r.status === 'Upcoming' || r.status === 'Today' ? (
                              <Button
                                size="small"
                                icon={<SwapOutlined />}
                                onClick={() => handleOpenRequestModal(r)}
                                style={{ borderRadius: 6, fontSize: 12 }}
                              >
                                Request Change
                              </Button>
                            ) : (
                              <Text type="secondary" style={{ fontSize: 12 }}>--</Text>
                            )
                          )
                        }
                      ]}
                    />
                  )}
                </div>
              )
            },
            {
              key: 'calendar',
              label: (
                <span>
                  <CalendarOutlined />
                  Roster Calendar
                </span>
              ),
              children: (
                <div>
                  {/* Color Legend */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 12,
                    padding: '12px 16px',
                    background: '#f8fafc',
                    borderRadius: 12,
                    marginBottom: 16,
                    border: '1px solid #f1f5f9'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <ScheduleOutlined style={{ color: '#2563eb' }} />
                      <span style={{ fontWeight: 600, fontSize: 13, color: '#334155' }}>Roster Legend:</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
                      {[
                        { label: 'Day Shift (☀️)', dot: '#f59e0b', text: '#b45309' },
                        { label: 'Night Shift (🌙)', dot: '#8b5cf6', text: '#6d28d9' },
                        { label: 'Weekly Off (☕)', dot: '#0ea5e9', text: '#0369a1' },
                        { label: 'Holiday (🎉)', dot: '#ec4899', text: '#be185d' },
                        { label: 'Leave (🏖️)', dot: '#eab308', text: '#a16207' },
                      ].map(item => (
                        <div key={item.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: item.text, fontWeight: 500 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: item.dot, display: 'inline-block' }} />
                          <span>{item.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Weekday Header */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: 8,
                    textAlign: 'center',
                    marginBottom: 10
                  }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, idx) => (
                      <div
                        key={d}
                        style={{
                          padding: '10px 0',
                          background: idx === 0 || idx === 6 ? '#f1f5f9' : '#f8fafc',
                          borderRadius: 8,
                          fontWeight: 700,
                          fontSize: 12,
                          color: idx === 0 ? '#ef4444' : (idx === 6 ? '#2563eb' : '#475569'),
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em'
                        }}
                      >
                        {d}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: 8
                  }}>
                    {calendarGrid.map((cell) => {
                      if (cell.isEmpty) {
                        return (
                          <div
                            key={cell.key}
                            style={{
                              minHeight: 104,
                              background: '#fafafa',
                              borderRadius: 12,
                              border: '1px dashed #f1f5f9',
                              opacity: 0.35
                            }}
                          />
                        );
                      }

                      const r = cell.record;
                      const isToday = cell.date === dayjs().format('YYYY-MM-DD');

                      let bg = '#ffffff';
                      let borderColor = '#e2e8f0';

                      if (r?.dayType === 'WEEKLY_OFF') {
                        bg = '#f0f9ff';
                        borderColor = '#bae6fd';
                      } else if (r?.dayType === 'HOLIDAY') {
                        bg = '#faf5ff';
                        borderColor = '#e9d5ff';
                      } else if (r?.dayType === 'LEAVE') {
                        bg = '#fefce8';
                        borderColor = '#fef08a';
                      } else if (r?.isNightShift) {
                        bg = '#faf5ff';
                        borderColor = '#ddd6fe';
                      } else if (r?.dayType === 'SHIFT') {
                        bg = '#f0fdf4';
                        borderColor = '#bbf7d0';
                      }

                      return (
                        <div
                          key={cell.key}
                          onClick={() => setSelectedCalendarDay(r || { date: cell.date, shiftName: todayShift?.shiftName || 'Day Shift', time: todayShift?.time || '' })}
                          style={{
                            minHeight: 104,
                            padding: 10,
                            borderRadius: 12,
                            background: isToday ? '#ffffff' : bg,
                            border: isToday ? '2px solid #2563eb' : `1px solid ${borderColor}`,
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            boxShadow: isToday ? '0 4px 12px rgba(37, 99, 235, 0.15)' : 'none'
                          }}
                          className="roster-calendar-cell"
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            {isToday ? (
                              <span style={{
                                background: '#2563eb',
                                color: '#ffffff',
                                fontSize: 12,
                                fontWeight: 700,
                                padding: '2px 8px',
                                borderRadius: 10,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4
                              }}>
                                <span>{cell.dayNum}</span>
                                <span style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase' }}>Today</span>
                              </span>
                            ) : (
                              <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>
                                {cell.dayNum}
                              </span>
                            )}

                            {r && (
                              <span style={{ fontSize: 14 }}>
                                {r.dayType === 'WEEKLY_OFF'
                                  ? '☕'
                                  : r.dayType === 'HOLIDAY'
                                  ? '🎉'
                                  : r.dayType === 'LEAVE'
                                  ? '🏖️'
                                  : r.isNightShift
                                  ? '🌙'
                                  : '☀️'}
                              </span>
                            )}
                          </div>

                          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {r?.dayType === 'WEEKLY_OFF' ? (
                              <Tag color="blue" style={{ fontSize: 10, margin: 0, padding: '0 4px', borderRadius: 4, fontWeight: 600 }}>
                                Weekly Off
                              </Tag>
                            ) : r?.dayType === 'HOLIDAY' ? (
                              <Tag color="purple" style={{ fontSize: 10, margin: 0, padding: '0 4px', borderRadius: 4, fontWeight: 600 }}>
                                Holiday
                              </Tag>
                            ) : r?.dayType === 'LEAVE' ? (
                              <Tag color="gold" style={{ fontSize: 10, margin: 0, padding: '0 4px', borderRadius: 4, fontWeight: 600 }}>
                                Leave
                              </Tag>
                            ) : (
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {r?.shiftName || 'Day Shift'}
                                </div>
                                <div style={{ fontSize: 10, color: '#2563eb', fontWeight: 500 }}>
                                  {r?.time}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )
            },
            {
              key: 'requests',
              label: (
                <span>
                  <SwapOutlined />
                  My Change Requests
                  {changeRequests.length > 0 && (
                    <Badge count={changeRequests.length} style={{ marginLeft: 6, backgroundColor: '#10b981' }} />
                  )}
                </span>
              ),
              children: (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div>
                      <Title level={5} style={{ margin: 0 }}>Shift Change Requests</Title>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        Track your submitted shift change or weekly off swap requests
                      </Text>
                    </div>

                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => handleOpenRequestModal(null)}
                      style={{ borderRadius: 6 }}
                    >
                      New Request
                    </Button>
                  </div>

                  {changeRequests.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                      <SwapOutlined style={{ fontSize: 32, marginBottom: 8 }} />
                      <div>No shift change requests submitted.</div>
                    </div>
                  ) : (
                    <Table
                      dataSource={changeRequests}
                      rowKey="id"
                      pagination={{ pageSize: 10 }}
                      size="middle"
                      columns={[
                        {
                          title: 'Date',
                          dataIndex: 'date',
                          key: 'date',
                          render: (d) => <Text strong>{dayjs(d).format('DD MMM YYYY')}</Text>
                        },
                        {
                          title: 'Current Shift',
                          dataIndex: 'currentShift',
                          key: 'currentShift',
                          render: (cs) => cs?.name || 'Assigned Shift'
                        },
                        {
                          title: 'Requested Shift / Status',
                          key: 'reqShift',
                          render: (_, r) => (
                            <Tag color="blue" style={{ borderRadius: 6, fontWeight: 600 }}>
                              {r.requestedStatus === 'WEEKLY_OFF' ? 'Weekly Off' : (r.requestedShift?.name || 'Requested Shift')}
                            </Tag>
                          )
                        },
                        {
                          title: 'Reason',
                          dataIndex: 'reason',
                          key: 'reason'
                        },
                        {
                          title: 'Approval Status',
                          dataIndex: 'status',
                          key: 'status',
                          render: (s) => {
                            const conf = {
                              APPROVED: { color: 'green', text: 'Approved', icon: <CheckCircleOutlined /> },
                              PENDING: { color: 'gold', text: 'Pending Approval', icon: <ClockCircleOutlined /> },
                              REJECTED: { color: 'red', text: 'Rejected', icon: <CloseCircleOutlined /> },
                            }[s] || { color: 'default', text: s };

                            return (
                              <Tag color={conf.color} icon={conf.icon} style={{ borderRadius: 6, fontWeight: 600 }}>
                                {conf.text}
                              </Tag>
                            );
                          }
                        },
                        {
                          title: 'Admin Remarks',
                          dataIndex: 'adminRemarks',
                          key: 'adminRemarks',
                          render: (txt) => txt || <Text type="secondary">--</Text>
                        }
                      ]}
                    />
                  )}
                </div>
              )
            }
          ]}
        />
      </Card>

      {/* 4. MODAL: Request Roster Change */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SwapOutlined style={{ color: '#2563eb' }} />
            <span>Request Shift Change</span>
          </div>
        }
        open={requestModalVisible}
        onCancel={() => setRequestModalVisible(false)}
        onOk={() => requestForm.submit()}
        confirmLoading={submittingRequest}
        okText="Submit Request"
      >
        <Form
          form={requestForm}
          layout="vertical"
          onFinish={handleSubmitChangeRequest}
          initialValues={{
            requestedStatus: 'SHIFT'
          }}
        >
          <Form.Item
            name="date"
            label="Date to Change Shift"
            rules={[{ required: true, message: 'Please select the date' }]}
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item
            name="requestedStatus"
            label="Request Type"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="SHIFT">Request Another Shift Template</Option>
              <Option value="WEEKLY_OFF">Request Weekly Off on this Date</Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.requestedStatus !== cur.requestedStatus}
          >
            {({ getFieldValue }) =>
              getFieldValue('requestedStatus') === 'SHIFT' ? (
                <Form.Item
                  name="requestedShiftTemplateId"
                  label="Select Requested Shift"
                  rules={[{ required: true, message: 'Please select a shift template' }]}
                >
                  <Select placeholder="Select shift">
                    {activeShifts.map(s => (
                      <Option key={s.id} value={s.id}>
                        {s.name} ({s.startTime?.slice(0, 5)} - {s.endTime?.slice(0, 5)})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item
            name="reason"
            label="Reason for Shift Change"
            rules={[{ required: true, message: 'Please provide reason' }]}
          >
            <TextArea rows={3} placeholder="e.g. Urgent family emergency, personal appointment, or mutually agreed swap" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 5. MODAL: Day Details Popup */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarOutlined style={{ color: '#2563eb' }} />
            <span>{selectedCalendarDay?.date ? dayjs(selectedCalendarDay.date).format('dddd, DD MMMM YYYY') : 'Day Details'}</span>
          </div>
        }
        open={!!selectedCalendarDay}
        onCancel={() => setSelectedCalendarDay(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedCalendarDay(null)}>
            Close
          </Button>,
          <Button
            key="req"
            type="primary"
            icon={<SwapOutlined />}
            onClick={() => {
              const d = selectedCalendarDay;
              setSelectedCalendarDay(null);
              handleOpenRequestModal(d);
            }}
            style={{ background: '#2563eb' }}
          >
            Request Shift Change
          </Button>
        ]}
      >
        {selectedCalendarDay && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '10px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text type="secondary">Assigned Shift:</Text>
              <Text strong style={{ fontSize: 15, color: '#0f172a' }}>{selectedCalendarDay.shiftName}</Text>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">Shift Timing:</Text>
              <Text style={{ fontWeight: 600, color: '#2563eb' }}>{selectedCalendarDay.time || '--'}</Text>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">Shift Type:</Text>
              <Tag color={selectedCalendarDay.isNightShift ? 'purple' : 'gold'}>
                {selectedCalendarDay.shiftType || 'Day Shift'}
              </Tag>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">Schedule Status:</Text>
              <Tag color={selectedCalendarDay.status === 'Today' ? 'green' : 'blue'}>
                {selectedCalendarDay.status}
              </Tag>
            </div>
          </div>
        )}
      </Modal>
    </StaffLayout>
  );
};

export default StaffRoster;